<template>
  <div ref="mapContainer" :style="{ height: height, width: width }"></div>
</template>

<script>
import { extractRouteFromHereResponse } from '@/utils/herePolylineDecoder'

export default {
  name: 'HereMap',
  props: {
    center: {
      type: Object,
      default: () => ({ lat: 45.508, lng: -73.587 })
    },
    zoom: {
      type: Number,
      default: 7
    },
    height: {
      type: String,
      default: '500px'
    },
    width: {
      type: String,
      default: '100%'
    },
    markers: {
      type: Array,
      default: () => []
    },
    routeData: {
      type: String,
      default: null
    },
    apiKey: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      map: null,
      platform: null,
      markersGroup: null,
      routeGroup: null,
      mapBehavior: null,
      mapUI: null
    }
  },
  computed: {
    mapLanguage () {
      // Pobierz język z vue-i18n (reaktywny, format 1-częściowy: pl, en)
      return this.$i18n?.locale || navigator.language.split('-')[0] || 'pl'
    },
    userLocale () {
      // Konstruuj format 2-częściowy z mapLanguage (pl -> pl-PL, en -> en-US)
      // To zapewnia reaktywność, bo zależy tylko od mapLanguage
      const lang = this.mapLanguage
      return `${lang}-${lang.toUpperCase()}`
    }
  },
  watch: {
    center: {
      handler (newCenter) {
        if (this.map && newCenter) {
          this.map.setCenter(new window.H.geo.Point(newCenter.lat, newCenter.lng))
        }
      },
      deep: true
    },
    markers: {
      handler () {
        this.updateMarkers()
      },
      deep: true
    },
    routeData: {
      handler () {
        this.updateRoute()
      }
    },
    mapLanguage () {
      // Gdy zmieni się język użytkownika (reaktywny przez $i18n.locale), przeładuj mapę
      if (this.map) {
        this.map.dispose()
        this.initializeMap()
      }
    }
  },
  async mounted () {
    await this.loadHereAPI()
    this.initializeMap()
  },
  beforeDestroy () {
    if (this.map) {
      this.map.dispose()
    }
  },
  methods: {
    async loadHereAPI () {
      return new Promise((resolve, reject) => {
        // Sprawdź czy HERE API już załadowane
        if (window.H) {
          resolve()
          return
        }

        // Załaduj HERE Maps API Core
        const coreScript = document.createElement('script')
        coreScript.src = 'https://js.api.here.com/v3/3.1/mapsjs-core.js'
        coreScript.onload = () => {
          // Załaduj HERE Maps API Service
          const serviceScript = document.createElement('script')
          serviceScript.src = 'https://js.api.here.com/v3/3.1/mapsjs-service.js'
          serviceScript.onload = () => {
            // Załaduj HERE Maps API UI
            const uiScript = document.createElement('script')
            uiScript.src = 'https://js.api.here.com/v3/3.1/mapsjs-ui.js'
            uiScript.onload = () => {
              // Załaduj HERE Maps API MapEvents
              const mapEventsScript = document.createElement('script')
              mapEventsScript.src = 'https://js.api.here.com/v3/3.1/mapsjs-mapevents.js'
              mapEventsScript.onload = () => {
                // Załaduj CSS
                const cssLink = document.createElement('link')
                cssLink.rel = 'stylesheet'
                cssLink.href = 'https://js.api.here.com/v3/3.1/mapsjs-ui.css'
                document.head.appendChild(cssLink)
                resolve()
              }
              mapEventsScript.onerror = reject
              document.head.appendChild(mapEventsScript)
            }
            uiScript.onerror = reject
            document.head.appendChild(uiScript)
          }
          serviceScript.onerror = reject
          document.head.appendChild(serviceScript)
        }
        coreScript.onerror = reject
        document.head.appendChild(coreScript)
      })
    },

    initializeMap () {
      // Inicjalizuj platformę HERE
      this.platform = new window.H.service.Platform({
        apikey: this.apiKey
      })

      // Pobierz domyślne warstwy map z ustawieniem języka użytkownika
      const defaultLayers = this.platform.createDefaultLayers({
        lg: this.mapLanguage // Ustawienie języka dla etykiet mapy (format 1-częściowy: pl, en)
      })

      // Inicjalizuj mapę
      this.map = new window.H.Map(
        this.$refs.mapContainer,
        defaultLayers.vector.normal.map,
        {
          zoom: this.zoom,
          center: { lat: this.center.lat, lng: this.center.lng }
        }
      )

      // Dodaj interakcje z mapą (zoom, pan, etc.)
      const behavior = new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(this.map))
      const ui = window.H.ui.UI.createDefault(this.map, defaultLayers)

      // Zapisz referencje (opcjonalnie można użyć później)
      this.mapBehavior = behavior
      this.mapUI = ui

      // Stwórz grupy dla markerów i tras
      this.markersGroup = new window.H.map.Group()
      this.routeGroup = new window.H.map.Group()
      this.map.addObject(this.markersGroup)
      this.map.addObject(this.routeGroup)

      // Dodaj początkowe markery i trasę
      this.updateMarkers()
      this.updateRoute()

      this.$emit('map-ready', this.map)
    },

    updateMarkers () {
      if (!this.map || !this.markersGroup) return

      // Usuń istniejące markery
      this.markersGroup.removeAll()

      // Dodaj nowe markery
      this.markers.forEach((marker, index) => {
        const position = new window.H.geo.Point(marker.position.lat, marker.position.lng)
        const icon = this.createColoredMarkerIcon(marker.type, marker.action)
        const hereMarker = new window.H.map.Marker(position, { icon })

        if (marker.title) {
          hereMarker.setData(marker.title)
        }

        this.markersGroup.addObject(hereMarker)
      })
    },

    createColoredMarkerIcon (type, action) {
      // Wybierz kolor na podstawie typu i akcji
      let color = '#1976d2' // domyślny niebieski

      if (type === 'filter') {
        color = action === 'loading' ? '#2e7d32' : '#66bb6a' // ciemny zielony : jasny zielony
      } else if (type === 'selected') {
        color = action === 'loading' ? '#1565c0' : '#42a5f5' // ciemny niebieski : jasny niebieski
      }

      // Użyj standardowego podejścia HERE Maps do kolorowych markerów
      const canvas = document.createElement('canvas')
      canvas.width = 31 // Powiększone o 30% (24 * 1.3)
      canvas.height = 39 // Powiększone o 30% (30 * 1.3)
      const ctx = canvas.getContext('2d')

      // Narysuj kształt kropli (marker)
      ctx.fillStyle = color
      ctx.beginPath()

      // Górna okrągła część (przeskalowana o 30%)
      ctx.arc(16, 12, 10, 0, 2 * Math.PI)
      ctx.fill()

      // Dolny trójkątny "ogon" kropli (przeskalowany o 30%)
      ctx.beginPath()
      ctx.moveTo(16, 33) // Spiczasty koniec na dole
      ctx.lineTo(8, 20) // Lewy róg
      ctx.lineTo(24, 20) // Prawy róg
      ctx.closePath()
      ctx.fill()

      // Biała obwódka
      ctx.strokeStyle = 'white'
      ctx.lineWidth = 2

      // Obwódka górnej części
      ctx.beginPath()
      ctx.arc(16, 12, 10, 0, 2 * Math.PI)
      ctx.stroke()

      // Obwódka dolnej części
      ctx.beginPath()
      ctx.moveTo(16, 33)
      ctx.lineTo(8, 20)
      ctx.lineTo(24, 20)
      ctx.closePath()
      ctx.stroke()

      // Białe środek (punkt na mapie) - przeskalowane o 30%
      ctx.fillStyle = 'white'
      ctx.beginPath()
      ctx.arc(16, 12, 4, 0, 2 * Math.PI)
      ctx.fill()

      return new window.H.map.Icon(canvas.toDataURL(), {
        size: { w: 31, h: 39 }, // Powiększone o 30%
        anchor: { x: 16, y: 33 } // Punkt zakotwiczenia przeskalowany o 30%
      })
    },

    updateRoute () {
      if (!this.map || !this.routeGroup || !this.routeData) return

      try {
        // Usuń istniejące trasy
        this.routeGroup.removeAll()

        // Ekstraktuj dane trasy używając helpera
        const { coordinates, routeInfo } = extractRouteFromHereResponse(this.routeData)

        if (coordinates && coordinates.length > 1) {
          // Shapes from arbitrary geographic points - zgodnie z dokumentacją HERE
          const flatCoordinates = []

          coordinates.forEach(point => {
            if (point &&
                typeof point.lat === 'number' &&
                typeof point.lng === 'number' &&
                !isNaN(point.lat) &&
                !isNaN(point.lng)) {
              flatCoordinates.push({ lat: point.lat, lng: point.lng })
            }
          })

          if (flatCoordinates.length >= 4) { // Minimum 2 punkty (4 współrzędne)
            // Użyj H.geo.Strip zgodnie z dokumentacją dla arbitrary points
            var linestring = new window.H.geo.LineString()
            flatCoordinates.forEach(function (point) {
              linestring.pushPoint(point)
            })

            const routePolyline = new window.H.map.Polyline(linestring, {
              style: {
                strokeColor: '#1976d2',
                lineWidth: 4
              }
            })

            this.routeGroup.addObject(routePolyline)

            // Dopasuj widok do trasy
            this.map.getViewModel().setLookAtData({
              bounds: this.routeGroup.getBoundingBox()
            })

            // Emituj informacje o trasie
            if (routeInfo) {
              this.$emit('route-loaded', routeInfo)
            }
          }
        }
      } catch (error) {
        console.error('Error parsing route data:', error)
      }
    },

    // Publiczne metody do użycia przez komponent nadrzędny
    setCenter (center) {
      if (this.map) {
        this.map.setCenter(new window.H.geo.Point(center.lat, center.lng))
      }
    },

    setZoom (zoom) {
      if (this.map) {
        this.map.getViewModel().setZoom(zoom)
      }
    },

    addMarker (position, title = '', type = 'default', action = 'default') {
      if (!this.map || !this.markersGroup) return

      const markerPosition = new window.H.geo.Point(position.lat, position.lng)
      const icon = this.createColoredMarkerIcon(type, action)
      const marker = new window.H.map.Marker(markerPosition, { icon })

      if (title) {
        marker.setData(title)
      }

      this.markersGroup.addObject(marker)
      return marker
    },

    removeAllMarkers () {
      if (this.markersGroup) {
        this.markersGroup.removeAll()
      }
    }
  }
}
</script>

<style scoped>
/* Stylizacja kontenera mapy */
</style>
